import { ASKABLE_STUDIO_DEV_URL, ASKABLE_STUDIO_URL } from 'shared-utils';
import { match, P } from 'ts-pattern';

import type { StatsigTier } from 'feature-flags';

const safeWindow = typeof window === 'undefined' ? ({} as Partial<Window>) : window;

// The deployment this bundle is running on, keyed off the hostname because one Vercel
// build is served from several of them. Deliberately finer-grained than either consumer
// needs on its own — Statsig folds demo into the production gate set, while Datadog keeps
// them apart — so each projection below stays a readable table instead of a second
// hostname matcher that can drift out of step with this one.
export type DeploymentTarget = 'production' | 'demo' | 'dev' | 'preview' | 'local';

export function resolveDeploymentTarget(
  hostname: string | undefined,
  isProductionDeployment: boolean,
): DeploymentTarget {
  return (
    match(hostname)
      .with('sessions.askable.com', () => 'production' as const)
      .with('sessions-demo.askable.com', () => 'demo' as const)
      .with('sessions-dev.askable.com', () => 'dev' as const)
      .with(P.nullish, () => 'local' as const)
      .with(P.union('localhost', '127.0.0.1', '0.0.0.0'), () => 'local' as const)
      // An unlisted hostname is a Vercel-generated alias or a domain added since this list
      // was written. Defer to the deployment itself rather than assuming non-production:
      // tagging real traffic as preview drops it out of every production dashboard.
      .otherwise(() => (isProductionDeployment ? 'production' : 'preview'))
  );
}

// Which gate set to evaluate. Demo runs the production gates so it demos shipped
// behaviour; dev is the only target on the staging set.
const STATSIG_TIER: Record<DeploymentTarget, StatsigTier> = {
  production: 'production',
  demo: 'production',
  dev: 'staging',
  preview: 'development',
  local: 'development',
};

// The RUM `env` tag. Values match the vocabulary the participant and admin apps already
// report, so one query spans every Askable frontend — which is why `dev` is `development`
// here despite evaluating the staging gate set.
//
// Demo reports as production: it serves the production build against production data, and
// existing dashboards and monitors filter on `env:production`.
const DATADOG_ENV: Record<DeploymentTarget, string> = {
  production: 'production',
  demo: 'production',
  dev: 'development',
  preview: 'preview',
  local: 'local',
};

// Studio keys a session on its study, so a room on dev data only resolves on dev Studio.
const STUDIO_URL: Record<DeploymentTarget, string> = {
  production: ASKABLE_STUDIO_URL,
  demo: ASKABLE_STUDIO_URL,
  dev: ASKABLE_STUDIO_DEV_URL,
  preview: ASKABLE_STUDIO_DEV_URL,
  local: ASKABLE_STUDIO_DEV_URL,
};

export const toStatsigTier = (target: DeploymentTarget): StatsigTier => STATSIG_TIER[target];

export const toDatadogEnv = (target: DeploymentTarget): string => DATADOG_ENV[target];

export const toStudioUrl = (target: DeploymentTarget): string => STUDIO_URL[target];

const isDemo =
  process.env.VERCEL_ENV !== 'production' &&
  (safeWindow?.location?.hostname === 'sessions-demo.askable.com' ||
    safeWindow?.localStorage?.getItem('isDemo') === 'true');

const isProductionDeployment =
  process.env.VERCEL_ENV === 'production' || process.env.NEXT_PUBLIC_VERCEL_ENV === 'production';

const deploymentTarget = resolveDeploymentTarget(safeWindow?.location?.hostname, isProductionDeployment);

const environment = {
  isProductionDeployment,
  isProductionBuild: process.env.NODE_ENV === 'production',
  isDemo,
  statsigClientKey: process.env.NEXT_PUBLIC_STATSIG_CLIENT_KEY,
  statsigTier: toStatsigTier(deploymentTarget),
  datadogEnv: toDatadogEnv(deploymentTarget),
  studioUrl: toStudioUrl(deploymentTarget),
};

export { environment };
